import {
  ACTIVITY_SEARCH_QUERY_MAX_LENGTH,
  type ActivityAppointmentLine,
  type ActivityCompleteness,
  type ActivityFreshness,
  type ActivityItem,
  type ActivityPointer,
  type AppointmentActivityItem,
} from "./activityContracts";
import {
  isActivityIdentifier,
  isSafeActivityInternalPath,
  isValidActivityEpochMs,
  validateActivityItem,
} from "./activityValidation";
import { captureActivityPlainData } from "./activityPlainDataCapture";

export interface ActivityRebookCatalogAddOn {
  groupId: string;
  addOnId: string;
  onlineBookable: boolean;
}

export interface ActivityRebookCatalogService {
  serviceId: string;
  onlineBookable: boolean;
  variantIds: readonly string[];
  addOns: readonly ActivityRebookCatalogAddOn[];
}

export interface ActivityRebookBundleChildVariantV1 {
  schemaVersion: 1;
  variantId: string;
}

export interface ActivityRebookCatalogBundleChild {
  childId: string;
  serviceId: string;
  /** Exact authored variant for this current bundle row, never inferred from history. */
  variantV1?: ActivityRebookBundleChildVariantV1;
  onlineBookable: boolean;
  addOns: readonly ActivityRebookCatalogAddOn[];
}

export interface ActivityRebookCatalogBundle {
  bundleId: string;
  schedule: "sequential" | "parallel";
  onlineBookable: boolean;
  children: readonly ActivityRebookCatalogBundleChild[];
}

export interface ActivityRebookCatalogAssignment {
  userLocationId: string;
  available: boolean;
  onlineBookingEnabled: boolean;
  safeSelectionPath: string;
  services: readonly ActivityRebookCatalogService[];
  bundles: readonly ActivityRebookCatalogBundle[];
}

export interface ActivityRebookCatalogLocation {
  locationId: string;
  available: boolean;
  public: boolean;
  onlineBookingEnabled: boolean;
  assignments: readonly ActivityRebookCatalogAssignment[];
}

export interface ActivityRebookCatalogSnapshot {
  catalogRevision: string;
  completeness: ActivityCompleteness;
  freshness: ActivityFreshness;
  locations: readonly ActivityRebookCatalogLocation[];
}

export type ActivityRebookDisabledReason =
  | "not-appointment"
  | "customer-mismatch"
  | "stale-activity"
  | "partial-activity"
  | "catalog-stale"
  | "catalog-partial"
  | "location-unavailable"
  | "specialist-unavailable"
  | "catalog-unavailable"
  | "online-booking-unavailable"
  | "service-unavailable"
  | "configuration-unavailable";

export interface ActivityRebookSourceAddOn {
  groupId: string;
  addOnId: string;
  quantity: number;
}

export type ActivityRebookSourceLine =
  | {
      kind: "service";
      lineId: string;
      serviceId: string;
      variantId: string | null;
      addOns: readonly ActivityRebookSourceAddOn[];
    }
  | {
      kind: "bundle";
      lineId: string;
      bundleId: string;
      schedule: "sequential" | "parallel";
      children: readonly {
        childId: string;
        serviceId: string;
        variantV1?: ActivityRebookBundleChildVariantV1;
        addOns: readonly ActivityRebookSourceAddOn[];
      }[];
    };

export type ActivityRebookResolution =
  | {
      status: "allowed";
      sourcePointer: ActivityPointer;
      customerAccountId: string;
      catalogRevision: string;
      locationId: string;
      userLocationId: string;
      destination: {
        kind: "internal-route";
        path: string;
      };
      serviceIds: readonly string[];
      sourceLines: readonly ActivityRebookSourceLine[];
    }
  | {
      status: "disabled";
      reason: ActivityRebookDisabledReason;
    };

export type ActivityRebookResolver = (
  item: ActivityItem,
  activeCustomerAccountId: string,
  evaluatedAtEpochMs?: number,
) => ActivityRebookResolution;

export interface ActivityRebookResolutionExpectation {
  item: ActivityItem;
  customerAccountId: string;
  evaluatedAtEpochMs?: number;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]) {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length &&
    actual.every((key) => keys.includes(key))
  );
}

function isDenseArray(
  value: unknown,
  minimum: number,
  maximum: number,
): value is readonly unknown[] {
  if (
    !Array.isArray(value) ||
    value.length < minimum ||
    value.length > maximum ||
    Object.keys(value).length !== value.length
  ) {
    return false;
  }

  for (let index = 0; index < value.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value, index)) {
      return false;
    }
  }

  return true;
}

function cloneAndFreeze<T>(value: T): T {
  if (Array.isArray(value)) {
    return Object.freeze(value.map(cloneAndFreeze)) as T;
  }
  if (isRecord(value)) {
    return Object.freeze(
      Object.fromEntries(
        Object.entries(value).map(([key, entry]) => [
          key,
          cloneAndFreeze(entry),
        ]),
      ),
    ) as T;
  }
  return value;
}

function resolveEvaluationEpochMs(value: number | undefined): number | null {
  const evaluatedAtEpochMs = value ?? Date.now();
  return isValidActivityEpochMs(evaluatedAtEpochMs)
    ? evaluatedAtEpochMs
    : null;
}

function isCurrentActivityFreshness(
  freshness: ActivityFreshness,
  evaluatedAtEpochMs: number,
) {
  return (
    freshness.status === "fresh" &&
    evaluatedAtEpochMs < freshness.staleAtEpochMs
  );
}

function projectActivityRebookAddOns(
  addOns: readonly {
    groupId: string;
    addOnId: string;
    quantity: number;
  }[],
) {
  return Object.freeze(
    addOns.map((addOn) =>
      Object.freeze({
        groupId: addOn.groupId,
        addOnId: addOn.addOnId,
        quantity: addOn.quantity,
      }),
    ),
  );
}

function projectActivityRebookSourceLines(
  lines: AppointmentActivityItem["lines"],
): readonly ActivityRebookSourceLine[] {
  return Object.freeze(
    lines.map((line) =>
      line.kind === "service"
        ? Object.freeze({
            kind: "service" as const,
            lineId: line.lineId,
            serviceId: line.serviceId,
            variantId: line.variantId,
            addOns: projectActivityRebookAddOns(line.addOns),
          })
        : Object.freeze({
            kind: "bundle" as const,
            lineId: line.lineId,
            bundleId: line.bundleId,
            schedule: line.schedule,
            children: Object.freeze(
              line.children.map((child) =>
                Object.freeze({
                  childId: child.childId,
                  serviceId: child.serviceId,
                  ...(child.variantV1 ? { variantV1: Object.freeze({ schemaVersion: 1 as const, variantId: child.variantV1.variantId }) } : {}),
                  addOns: projectActivityRebookAddOns(child.addOns),
                }),
              ),
            ),
          }),
    ),
  );
}

function activityRebookAddOnsMatch(
  value: unknown,
  expected: readonly ActivityRebookSourceAddOn[],
) {
  return (
    isDenseArray(value, 0, 100) &&
    value.length === expected.length &&
    value.every((entry, index) => {
      const expectedEntry = expected[index];
      return (
        expectedEntry !== undefined &&
        isRecord(entry) &&
        hasExactKeys(entry, ["groupId", "addOnId", "quantity"]) &&
        isActivityIdentifier(entry.groupId) &&
        isActivityIdentifier(entry.addOnId) &&
        Number.isSafeInteger(entry.quantity) &&
        Number(entry.quantity) > 0 &&
        entry.groupId === expectedEntry.groupId &&
        entry.addOnId === expectedEntry.addOnId &&
        entry.quantity === expectedEntry.quantity
      );
    })
  );
}

function activityRebookSourceLineMatches(
  value: unknown,
  expected: ActivityRebookSourceLine,
) {
  if (!isRecord(value) || value.kind !== expected.kind) {
    return false;
  }

  if (expected.kind === "service") {
    return (
      hasExactKeys(value, [
        "kind",
        "lineId",
        "serviceId",
        "variantId",
        "addOns",
      ]) &&
      isActivityIdentifier(value.lineId) &&
      isActivityIdentifier(value.serviceId) &&
      (value.variantId === null || isActivityIdentifier(value.variantId)) &&
      value.lineId === expected.lineId &&
      value.serviceId === expected.serviceId &&
      value.variantId === expected.variantId &&
      activityRebookAddOnsMatch(value.addOns, expected.addOns)
    );
  }

  if (
    !hasExactKeys(value, [
      "kind",
      "lineId",
      "bundleId",
      "schedule",
      "children",
    ]) ||
    !isActivityIdentifier(value.lineId) ||
    !isActivityIdentifier(value.bundleId) ||
    (value.schedule !== "sequential" && value.schedule !== "parallel") ||
    value.lineId !== expected.lineId ||
    value.bundleId !== expected.bundleId ||
    value.schedule !== expected.schedule ||
    !isDenseArray(value.children, 1, 100) ||
    value.children.length !== expected.children.length
  ) {
    return false;
  }

  return value.children.every((child, index) => {
    const expectedChild = expected.children[index];
    return (
      expectedChild !== undefined &&
      isRecord(child) &&
      hasExactKeys(child, ["childId", "serviceId", "addOns", ...(expectedChild.variantV1 ? ["variantV1"] : [])]) &&
      (!expectedChild.variantV1 || (isBundleChildVariantV1(child.variantV1) && child.variantV1.variantId === expectedChild.variantV1.variantId)) &&
      isActivityIdentifier(child.childId) &&
      isActivityIdentifier(child.serviceId) &&
      child.childId === expectedChild.childId &&
      child.serviceId === expectedChild.serviceId &&
      activityRebookAddOnsMatch(child.addOns, expectedChild.addOns)
    );
  });
}

/**
 * Limits a provider-authored rebook destination to either the explicit
 * discovery fallback or the exact current specialist selector join.
 */
export function isSafeActivityRebookSelectionPath(
  value: unknown,
  locationId: unknown,
  userLocationId: unknown,
): value is string {
  if (
    !isSafeActivityInternalPath(value) ||
    !isActivityIdentifier(locationId) ||
    !isActivityIdentifier(userLocationId)
  ) {
    return false;
  }

  const parsed = new URL(value, "https://volera.local");
  if (parsed.hash !== "") {
    return false;
  }

  if (parsed.pathname === "/results") {
    const entries = [...parsed.searchParams.entries()];
    return (
      entries.length === 1 &&
      entries[0][0] === "search" &&
      entries[0][1] === entries[0][1].trim() &&
      entries[0][1].length > 0 &&
      Array.from(entries[0][1]).length <= ACTIVITY_SEARCH_QUERY_MAX_LENGTH
    );
  }

  return (
    parsed.pathname ===
      `/locations/${locationId}/specialists/${userLocationId}/services` &&
    parsed.search === ""
  );
}

const ACTIVITY_REBOOK_DISABLED_REASONS: readonly ActivityRebookDisabledReason[] =
  Object.freeze([
    "not-appointment",
    "customer-mismatch",
    "stale-activity",
    "partial-activity",
    "catalog-stale",
    "catalog-partial",
    "location-unavailable",
    "specialist-unavailable",
    "catalog-unavailable",
    "online-booking-unavailable",
    "service-unavailable",
    "configuration-unavailable",
  ]);

/**
 * Treats an injected rebook resolver as untrusted. An allowed result must
 * retain the exact source/customer and carry only validated exact IDs plus a
 * route that belongs to its location/assignment join.
 */
export function validateActivityRebookResolution(
  input: unknown,
  expectation: ActivityRebookResolutionExpectation,
):
  | { valid: true; resolution: ActivityRebookResolution }
  | {
      valid: false;
      reason: "invalid-activity-rebook-resolution";
    } {
  const invalid = Object.freeze({
    valid: false as const,
    reason: "invalid-activity-rebook-resolution" as const,
  });
  const captured = captureActivityPlainData(input);
  const value = captured.valid ? captured.value : null;
  const expectedItem = validateActivityItem(expectation.item);
  const evaluatedAtEpochMs = resolveEvaluationEpochMs(
    expectation.evaluatedAtEpochMs,
  );

  if (
    !expectedItem.valid ||
    !isActivityIdentifier(expectation.customerAccountId) ||
    !isRecord(value) ||
    typeof value.status !== "string"
  ) {
    return invalid;
  }

  if (value.status === "disabled") {
    if (
      !hasExactKeys(value, ["status", "reason"]) ||
      !ACTIVITY_REBOOK_DISABLED_REASONS.includes(
        value.reason as ActivityRebookDisabledReason,
      )
    ) {
      return invalid;
    }

    return Object.freeze({
      valid: true as const,
      resolution: Object.freeze({
        status: "disabled" as const,
        reason: value.reason as ActivityRebookDisabledReason,
      }),
    });
  }

  if (
    expectedItem.item.activityType !== "appointment" ||
    expectedItem.item.ownership.customerAccountId !==
      expectation.customerAccountId ||
    evaluatedAtEpochMs === null ||
    !isCurrentActivityFreshness(
      expectedItem.item.freshness,
      evaluatedAtEpochMs,
    ) ||
    expectedItem.item.completeness.status !== "complete" ||
    expectedItem.item.location.availability !== "available"
  ) {
    return invalid;
  }

  const expectedStaffIds = collectSpecificStaffIds(expectedItem.item);
  const expectedServiceIds = expectedItem.item.lines.flatMap((line) =>
    line.kind === "service"
      ? [line.serviceId]
      : line.children.map((child) => child.serviceId),
  );
  const expectedSourceLines = projectActivityRebookSourceLines(
    expectedItem.item.lines,
  );

  if (
    !expectedStaffIds ||
    expectedStaffIds.length !== 1 ||
    value.status !== "allowed" ||
    !hasExactKeys(value, [
      "status",
      "sourcePointer",
      "customerAccountId",
      "catalogRevision",
      "locationId",
      "userLocationId",
      "destination",
      "serviceIds",
      "sourceLines",
    ]) ||
    value.sourcePointer !== expectedItem.item.pointer ||
    value.customerAccountId !== expectation.customerAccountId ||
    !isActivityIdentifier(value.catalogRevision) ||
    !isActivityIdentifier(value.locationId) ||
    value.locationId !== expectedItem.item.location.locationId ||
    !isActivityIdentifier(value.userLocationId) ||
    value.userLocationId !== expectedStaffIds[0] ||
    !isRecord(value.destination) ||
    !hasExactKeys(value.destination, ["kind", "path"]) ||
    value.destination.kind !== "internal-route" ||
    !isSafeActivityRebookSelectionPath(
      value.destination.path,
      value.locationId,
      value.userLocationId,
    ) ||
    !isDenseArray(value.serviceIds, 1, 10_000) ||
    !value.serviceIds.every(isActivityIdentifier) ||
    value.serviceIds.length !== expectedServiceIds.length ||
    !value.serviceIds.every(
      (serviceId, index) => serviceId === expectedServiceIds[index],
    ) ||
    !isDenseArray(value.sourceLines, 1, 100) ||
    value.sourceLines.length !== expectedSourceLines.length ||
    !value.sourceLines.every((line, index) => {
      const expectedLine = expectedSourceLines[index];
      return (
        expectedLine !== undefined &&
        activityRebookSourceLineMatches(line, expectedLine)
      );
    })
  ) {
    return invalid;
  }

  return Object.freeze({
    valid: true as const,
    resolution: cloneAndFreeze(value as unknown as ActivityRebookResolution),
  });
}

function isCompleteness(value: unknown): value is ActivityCompleteness {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["status", "missingFields"]) &&
    isDenseArray(value.missingFields, 0, 1_000) &&
    ((value.status === "complete" &&
      value.missingFields.length === 0) ||
      (value.status === "partial" &&
        value.missingFields.length > 0 &&
        value.missingFields.every(isActivityIdentifier)))
  );
}

function isFreshness(value: unknown): value is ActivityFreshness {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "status",
      "observedAtEpochMs",
      "staleAtEpochMs",
      "reason",
    ]) &&
    isValidActivityEpochMs(value.observedAtEpochMs) &&
    isValidActivityEpochMs(value.staleAtEpochMs) &&
    value.staleAtEpochMs >= value.observedAtEpochMs &&
    ((value.status === "fresh" && value.reason === null) ||
      (value.status === "stale" &&
        (value.reason === "provider-marked-stale" ||
          value.reason === "revision-superseded")))
  );
}

function isCatalogAddOn(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["groupId", "addOnId", "onlineBookable"]) &&
    isActivityIdentifier(value.groupId) &&
    isActivityIdentifier(value.addOnId) &&
    typeof value.onlineBookable === "boolean"
  );
}

function areCatalogAddOns(value: unknown): boolean {
  return (
    isDenseArray(value, 0, 250) &&
    value.every(isCatalogAddOn) &&
    new Set(
      value.map((entry) =>
        isRecord(entry)
          ? `${String(entry.groupId)}~${String(entry.addOnId)}`
          : "",
      ),
    ).size === value.length
  );
}

function isCatalogService(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "serviceId",
      "onlineBookable",
      "variantIds",
      "addOns",
    ]) &&
    isActivityIdentifier(value.serviceId) &&
    typeof value.onlineBookable === "boolean" &&
    isDenseArray(value.variantIds, 0, 250) &&
    value.variantIds.every(isActivityIdentifier) &&
    new Set(value.variantIds).size === value.variantIds.length &&
    areCatalogAddOns(value.addOns)
  );
}

function isBundleChildVariantV1(value: unknown): value is ActivityRebookBundleChildVariantV1 {
  return isRecord(value) && hasExactKeys(value, ["schemaVersion", "variantId"]) &&
    value.schemaVersion === 1 && isActivityIdentifier(value.variantId);
}

function isCatalogBundleChild(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "childId",
      "serviceId",
      "onlineBookable",
      "addOns",
      ...(Object.hasOwn(value, "variantV1") ? ["variantV1"] : []),
    ]) &&
    (!Object.hasOwn(value, "variantV1") || isBundleChildVariantV1(value.variantV1)) &&
    isActivityIdentifier(value.childId) &&
    isActivityIdentifier(value.serviceId) &&
    typeof value.onlineBookable === "boolean" &&
    areCatalogAddOns(value.addOns)
  );
}

function isCatalogBundle(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "bundleId",
      "schedule",
      "onlineBookable",
      "children",
    ]) &&
    isActivityIdentifier(value.bundleId) &&
    (value.schedule === "sequential" || value.schedule === "parallel") &&
    typeof value.onlineBookable === "boolean" &&
    isDenseArray(value.children, 1, 100) &&
    value.children.every(isCatalogBundleChild) &&
    new Set(
      value.children.map((child) =>
        isRecord(child) ? child.childId : null,
      ),
    ).size === value.children.length
  );
}

function isCatalogAssignment(value: unknown, locationId: string): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "userLocationId",
      "available",
      "onlineBookingEnabled",
      "safeSelectionPath",
      "services",
      "bundles",
    ]) &&
    isActivityIdentifier(value.userLocationId) &&
    typeof value.available === "boolean" &&
    typeof value.onlineBookingEnabled === "boolean" &&
    isSafeActivityRebookSelectionPath(
      value.safeSelectionPath,
      locationId,
      value.userLocationId,
    ) &&
    isDenseArray(value.services, 0, 500) &&
    value.services.every(isCatalogService) &&
    new Set(
      value.services.map((service) =>
        isRecord(service) ? service.serviceId : null,
      ),
    ).size === value.services.length &&
    isDenseArray(value.bundles, 0, 250) &&
    value.bundles.every(isCatalogBundle) &&
    new Set(
      value.bundles.map((bundle) =>
        isRecord(bundle) ? bundle.bundleId : null,
      ),
    ).size === value.bundles.length
  );
}

function isCatalogLocation(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "locationId",
      "available",
      "public",
      "onlineBookingEnabled",
      "assignments",
    ]) ||
    !isActivityIdentifier(value.locationId) ||
    typeof value.available !== "boolean" ||
    typeof value.public !== "boolean" ||
    typeof value.onlineBookingEnabled !== "boolean" ||
    !isDenseArray(value.assignments, 0, 500)
  ) {
    return false;
  }

  return (
    value.assignments.every((assignment) =>
      isCatalogAssignment(assignment, value.locationId as string),
    ) &&
    new Set(
      value.assignments.map((assignment) =>
        isRecord(assignment) ? assignment.userLocationId : null,
      ),
    ).size === value.assignments.length
  );
}

export function validateActivityRebookCatalogSnapshot(
  input: unknown,
):
  | { valid: true; catalog: ActivityRebookCatalogSnapshot }
  | {
      valid: false;
      reason: "invalid-activity-rebook-catalog";
    } {
  const captured = captureActivityPlainData(input);
  const value = captured.valid ? captured.value : null;
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "catalogRevision",
      "completeness",
      "freshness",
      "locations",
    ]) ||
    !isActivityIdentifier(value.catalogRevision) ||
    !isCompleteness(value.completeness) ||
    !isFreshness(value.freshness) ||
    !isDenseArray(value.locations, 0, 100) ||
    !value.locations.every(isCatalogLocation) ||
    new Set(
      value.locations.map((location) =>
        isRecord(location) ? location.locationId : null,
      ),
    ).size !== value.locations.length
  ) {
    return Object.freeze({
      valid: false as const,
      reason: "invalid-activity-rebook-catalog" as const,
    });
  }

  return Object.freeze({
    valid: true as const,
    catalog: cloneAndFreeze(
      value as unknown as ActivityRebookCatalogSnapshot,
    ),
  });
}

function disabled(
  reason: ActivityRebookDisabledReason,
): ActivityRebookResolution {
  return Object.freeze({ status: "disabled" as const, reason });
}

function resolveLineAgainstAssignment(
  line: ActivityAppointmentLine,
  assignment: ActivityRebookCatalogAssignment,
): ActivityRebookDisabledReason | null {
  if (line.kind === "service") {
    const service = assignment.services.find(
      (candidate) => candidate.serviceId === line.serviceId,
    );
    if (!service || !service.onlineBookable) {
      return "service-unavailable";
    }

    if (
      (line.variantId !== null &&
        !service.variantIds.includes(line.variantId)) ||
      line.addOns.some(
        (addOn) =>
          !service.addOns.some(
            (candidate) =>
              candidate.groupId === addOn.groupId &&
              candidate.addOnId === addOn.addOnId &&
              candidate.onlineBookable,
          ),
      )
    ) {
      return "configuration-unavailable";
    }

    return null;
  }

  const bundle = assignment.bundles.find(
    (candidate) => candidate.bundleId === line.bundleId,
  );
  if (!bundle || !bundle.onlineBookable) {
    return "service-unavailable";
  }
  if (
    bundle.schedule !== line.schedule ||
    line.children.length !== bundle.children.length
  ) {
    return "configuration-unavailable";
  }

  for (const [index, historicalChild] of line.children.entries()) {
    const currentChild = bundle.children[index];
    if (
      !currentChild ||
      currentChild.childId !== historicalChild.childId ||
      currentChild.serviceId !== historicalChild.serviceId ||
      (currentChild.variantV1?.variantId ?? null) !== (historicalChild.variantV1?.variantId ?? null) ||
      !currentChild.onlineBookable
    ) {
      return "configuration-unavailable";
    }

    if (
      historicalChild.addOns.some(
        (addOn) =>
          !currentChild.addOns.some(
            (candidate) =>
              candidate.groupId === addOn.groupId &&
              candidate.addOnId === addOn.addOnId &&
              candidate.onlineBookable,
          ),
      )
    ) {
      return "configuration-unavailable";
    }
  }

  return null;
}

function collectSpecificStaffIds(
  appointment: AppointmentActivityItem,
): readonly string[] | null {
  const ids: string[] = [];

  for (const line of appointment.lines) {
    const staff =
      line.kind === "service"
        ? [line.staff]
        : line.children.map((child) => child.staff);

    for (const member of staff) {
      if (
        member.resolution !== "specific" ||
        member.availability !== "available"
      ) {
        return null;
      }
      ids.push(member.userLocationId);
    }
  }

  return Object.freeze([...new Set(ids)]);
}

/**
 * Revalidates historical exact IDs against an explicit current catalog.
 * The resolver returns only a safe selection destination. It never creates,
 * stores, or implies an appointment draft.
 */
export function resolveActivityRebook(input: {
  item: unknown;
  activeCustomerAccountId: string;
  catalog: ActivityRebookCatalogSnapshot;
  evaluatedAtEpochMs?: number;
}): ActivityRebookResolution {
  const validated = validateActivityItem(input.item);
  if (!validated.valid || validated.item.activityType !== "appointment") {
    return disabled("not-appointment");
  }

  const appointment = validated.item;
  const evaluatedAtEpochMs = resolveEvaluationEpochMs(
    input.evaluatedAtEpochMs,
  );
  if (
    appointment.ownership.customerAccountId !==
    input.activeCustomerAccountId
  ) {
    return disabled("customer-mismatch");
  }
  if (
    evaluatedAtEpochMs === null ||
    !isCurrentActivityFreshness(
      appointment.freshness,
      evaluatedAtEpochMs,
    )
  ) {
    return disabled("stale-activity");
  }
  if (appointment.completeness.status === "partial") {
    return disabled("partial-activity");
  }
  const catalogValidation =
    validateActivityRebookCatalogSnapshot(input.catalog);
  if (!catalogValidation.valid) {
    return disabled("catalog-unavailable");
  }
  const catalog = catalogValidation.catalog;
  if (!isCurrentActivityFreshness(catalog.freshness, evaluatedAtEpochMs)) {
    return disabled("catalog-stale");
  }
  if (catalog.completeness.status === "partial") {
    return disabled("catalog-partial");
  }
  if (appointment.location.availability !== "available") {
    return disabled("location-unavailable");
  }

  const location = catalog.locations.find(
    (candidate) =>
      candidate.locationId === appointment.location.locationId,
  );
  if (!location) {
    return disabled("catalog-unavailable");
  }
  if (!location.available || !location.public) {
    return disabled("location-unavailable");
  }
  if (!location.onlineBookingEnabled) {
    return disabled("online-booking-unavailable");
  }

  const historicalStaffIds = collectSpecificStaffIds(appointment);
  if (!historicalStaffIds || historicalStaffIds.length !== 1) {
    return disabled("specialist-unavailable");
  }

  const assignment = location.assignments.find(
    (candidate) =>
      candidate.userLocationId === historicalStaffIds[0],
  );
  if (!assignment || !assignment.available) {
    return disabled("specialist-unavailable");
  }
  if (!assignment.onlineBookingEnabled) {
    return disabled("online-booking-unavailable");
  }
  if (
    !isSafeActivityRebookSelectionPath(
      assignment.safeSelectionPath,
      location.locationId,
      assignment.userLocationId,
    )
  ) {
    return disabled("catalog-unavailable");
  }

  for (const line of appointment.lines) {
    const reason = resolveLineAgainstAssignment(line, assignment);
    if (reason) {
      return disabled(reason);
    }
  }

  const serviceIds = appointment.lines.flatMap((line) =>
    line.kind === "service"
      ? [line.serviceId]
      : line.children.map((child) => child.serviceId),
  );
  const sourceLines = projectActivityRebookSourceLines(
    appointment.lines,
  );

  return Object.freeze({
    status: "allowed" as const,
    sourcePointer: appointment.pointer,
    customerAccountId: input.activeCustomerAccountId,
    catalogRevision: catalog.catalogRevision,
    locationId: location.locationId,
    userLocationId: assignment.userLocationId,
    destination: Object.freeze({
      kind: "internal-route" as const,
      path: assignment.safeSelectionPath,
    }),
    serviceIds: Object.freeze(serviceIds),
    sourceLines,
  });
}
